import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiClock,
  FiBookOpen,
  FiAward,
  FiBarChart2,
  FiActivity,
  FiUser,
  FiArrowLeft,
  FiDownload,
  FiRefreshCw,
  FiChevronRight,
  FiZap
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import { useNotification } from '../../contexts/NotificationContext';
import { studentsAPI } from '../../services/api';
import '../../styles/student-progress.css';

const StudentProgress = () => {
  const { studentId } = useParams();
  const { showError } = useNotification();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [student, setStudent] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [timeframe, setTimeframe] = useState('month');

  // Load student progress data
  const loadProgressData = useCallback(async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch student basic info
      const studentResponse = await studentsAPI.getStudent(studentId);
      setStudent(studentResponse.data.student);

      // Fetch progress data from analytics endpoint
      const progressResponse = await fetch(`/api/analytics/progress/${studentId}?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const progressData = await progressResponse.json();
      if (progressData.success) {
        setProgressData(progressData.data);
      }

      // Fetch performance data
      const performanceResponse = await fetch(`/api/analytics/performance/${studentId}?period=month`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      await performanceResponse.json();

    } catch (error) {
      console.error('Error loading progress data:', error);
      showError('Failed to load student progress data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [studentId, timeframe, showError]);

  useEffect(() => {
    if (studentId) {
      loadProgressData();
    }
  }, [loadProgressData, studentId]);

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadProgressData(true);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${Math.round(value || 0)}%`;
  };

  // Format time duration
  const formatTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get progress level
  const getProgressLevel = (percentage) => {
    if (percentage >= 90) return { level: 'Excellent', variant: 'success', color: '#10B981' };
    if (percentage >= 80) return { level: 'Very Good', variant: 'success', color: '#059669' };
    if (percentage >= 70) return { level: 'Good', variant: 'warning', color: '#F59E0B' };
    if (percentage >= 60) return { level: 'Fair', variant: 'warning', color: '#D97706' };
    return { level: 'Needs Improvement', variant: 'danger', color: '#EF4444' };
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend > 0) return <FiTrendingUp className="trend-up" />;
    if (trend < 0) return <FiTrendingDown className="trend-down" />;
    return <FiActivity className="trend-neutral" />;
  };

  if (loading) {
    return (
      <div className="student-progress-loading">
        <LoadingSpinner size="large" />
        <p>Loading student progress...</p>
      </div>
    );
  }

  if (!student || !progressData) {
    return (
      <EmptyState
        icon={FiUser}
        title="Student Not Found"
        description="The requested student progress data could not be found."
        action={
          <Button onClick={() => navigate('/students')}>
            Back to Students
          </Button>
        }
      />
    );
  }

  return (
    <div className="student-progress">
      {/* Header */}
      <div className="progress-header">
        <div className="header-left">
          <Button
            variant="ghost"
            size="small"
            onClick={() => navigate('/students')}
            className="back-button"
          >
            <FiArrowLeft size={16} />
            Back to Students
          </Button>
          <div className="student-info">
            <div className="student-avatar">
              {student.avatar ? (
                <img src={student.avatar} alt={student.firstName} />
              ) : (
                <div className="avatar-placeholder">
                  {student.firstName?.[0]}{student.lastName?.[0]}
                </div>
              )}
            </div>
            <div className="student-details">
              <h1>{student.firstName} {student.lastName}</h1>
              <div className="student-meta">
                <span className="student-email">{student.email}</span>
                <Badge variant="info">{student.role}</Badge>
                {student.school && <span className="student-school">{student.school}</span>}
              </div>
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <div className="timeframe-selector">
            {['week', 'month', 'quarter', 'year'].map((period) => (
              <button
                key={period}
                className={`timeframe-btn ${timeframe === period ? 'active' : ''}`}
                onClick={() => handleTimeframeChange(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FiRefreshCw size={16} className={refreshing ? 'spinning' : ''} />
            Refresh
          </Button>
          
          <Button variant="outline" size="small">
            <FiDownload size={16} />
            Export Report
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="progress-overview">
        <div className="overview-card">
          <div className="card-header">
            <FiTrendingUp size={24} />
            <span>Overall Progress</span>
          </div>
          <div className="card-content">
            <div className="progress-circle">
              <div className="circle-progress" style={{ '--progress': `${progressData.overallProgress}%` }}>
                <span className="progress-value">{formatPercentage(progressData.overallProgress)}</span>
              </div>
            </div>
            <div className="progress-details">
              <Badge variant={getProgressLevel(progressData.overallProgress).variant}>
                {getProgressLevel(progressData.overallProgress).level}
              </Badge>
              <span className="progress-trend">
                {getTrendIcon(progressData.progressTrend)}
                {formatPercentage(Math.abs(progressData.progressTrend))} vs last period
              </span>
            </div>
          </div>
        </div>
        
        <div className="overview-card">
          <div className="card-header">
            <FiBookOpen size={24} />
            <span>Learning Stats</span>
          </div>
          <div className="card-content">
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{progressData.totalTests}</span>
                <span className="stat-label">Tests Taken</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatPercentage(progressData.averageScore)}</span>
                <span className="stat-label">Average Score</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatTime(progressData.totalStudyTime)}</span>
                <span className="stat-label">Study Time</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{progressData.studyStreak}</span>
                <span className="stat-label">Day Streak</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="overview-card">
          <div className="card-header">
            <FiAward size={24} />
            <span>Achievements</span>
          </div>
          <div className="card-content">
            <div className="achievements-list">
              {progressData.recentAchievements?.slice(0, 3).map((achievement, index) => (
                <div key={index} className="achievement-item">
                  <div className="achievement-icon">
                    <FiAward size={16} />
                  </div>
                  <div className="achievement-details">
                    <span className="achievement-title">{achievement.title}</span>
                    <span className="achievement-date">{formatDate(achievement.date)}</span>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="text" size="small">
              View All Achievements
              <FiChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Subject Progress */}
      {progressData.subjectProgress && (
        <div className="subject-progress">
          <h3>Progress by Subject</h3>
          <div className="subject-list">
            {progressData.subjectProgress.map((subject, index) => {
              const progressLevel = getProgressLevel(subject.progress);
              return (
                <div key={index} className="subject-progress-item">
                  <div className="subject-info">
                    <div className="subject-header">
                      <h4>{subject.name}</h4>
                      <Badge variant={progressLevel.variant}>
                        {progressLevel.level}
                      </Badge>
                    </div>
                    <div className="subject-stats">
                      <span className="stat">
                        <FiBookOpen size={14} />
                        {subject.testsCompleted} tests completed
                      </span>
                      <span className="stat">
                        <FiClock size={14} />
                        {formatTime(subject.timeSpent)}
                      </span>
                      <span className="stat">
                        <FiTarget size={14} />
                        {formatPercentage(subject.averageScore)} avg score
                      </span>
                    </div>
                  </div>
                  
                  <div className="subject-progress-bar">
                    <div className="progress-track">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${subject.progress}%`,
                          backgroundColor: progressLevel.color
                        }}
                      ></div>
                    </div>
                    <span className="progress-percentage">
                      {formatPercentage(subject.progress)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {progressData.recentActivity && (
        <div className="recent-activity">
          <h3>Recent Activity</h3>
          <div className="activity-timeline">
            {progressData.recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-marker">
                  <div className={`marker-dot ${activity.type}`}></div>
                </div>
                <div className="activity-content">
                  <div className="activity-header">
                    <h4>{activity.title}</h4>
                    <span className="activity-date">{formatDate(activity.date)}</span>
                  </div>
                  <p className="activity-description">{activity.description}</p>
                  {activity.score && (
                    <div className="activity-score">
                      <Badge variant={activity.score >= 80 ? 'success' : activity.score >= 60 ? 'warning' : 'danger'}>
                        Score: {formatPercentage(activity.score)}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Chart */}
      {progressData.performanceChart && (
        <div className="performance-chart">
          <h3>Performance Over Time</h3>
          <div className="chart-container">
            <div className="chart-placeholder">
              <FiBarChart2 size={48} />
              <p>Performance chart will be rendered here</p>
              <small>Chart implementation requires a charting library like Chart.js or Recharts</small>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {progressData.recommendations && (
        <div className="recommendations">
          <h3>Recommendations</h3>
          <div className="recommendations-list">
            {progressData.recommendations.map((recommendation, index) => (
              <div key={index} className={`recommendation-item ${recommendation.priority}`}>
                <div className="recommendation-icon">
                  <FiZap size={20} />
                </div>
                <div className="recommendation-content">
                  <h4>{recommendation.title}</h4>
                  <p>{recommendation.description}</p>
                  <Badge variant={recommendation.priority === 'high' ? 'danger' : recommendation.priority === 'medium' ? 'warning' : 'info'}>
                    {recommendation.priority} priority
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProgress;